import parser from 'any-date-parser';

import {
  Button,
  Divider,
  Grid,
  Group,
  Slider,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { closeModal } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { handleSubmitError } from '@utils/forms';

import {
  CreatePatientRequestBody,
  Patient,
  UpdatePatientRequest,
} from '@interfaces/patients.interface';

import {
  useCreatePatientMutation,
  useUpdatePatientMutation,
} from '@api/patients.api';

import useFormEnhanced from '@hooks/useFormEnhanced';
import useUserPermissionGuard from '@hooks/useUserPermissionGuard';

import {
  CitySelector,
  NationSelector,
  ProvinceSelector,
  ZipSelector,
} from '@components/common/locations';
import EthnicitySelector from '@components/ethnicities/EthnicitySelector';
import GenderSelector from '@components/genders/GenderSelector';
import MedicalDoctorsSelector from '@components/medicalDoctors/MedicalDoctorsSelector';
import MedicalOrganizationsSelector from '@components/medicalOrganizations/MedicalOrganizationsSelector';
import PathologiesSelector from '@components/pathologies/PathologiesSelector';

export const UPSERT_PATIENT_FORM_MODAL_ID = 'upsertPatient';

interface UpsertPatientFormProps {
  patient?: Patient;
  afterSubmit?: (newPatient: Patient) => void;
}

export default function UpsertPatientForm({
  patient,
  afterSubmit,
}: UpsertPatientFormProps) {
  const canReadAllMedicalOrganizations = useUserPermissionGuard(
    'MEDICAL_ORGANIZATIONS.READ',
    false,
  );
  const canReadAllDoctors = useUserPermissionGuard(
    'MEDICAL_DOCTORS.READ',
    false,
  );

  const initialValues = {
    firstName: patient?.firstName || '',
    lastName: patient?.lastName || '',
    title: patient?.title || '',
    birthDate: patient?.birthDate ? new Date(patient.birthDate) : undefined,
    streetAddress1: patient?.streetAddress1 || '',
    streetAddress2: patient?.streetAddress2 || '',
    postalCode: patient?.postalCode || '',
    locality: patient?.locality || '',
    stateProvince: patient?.stateProvince || '',
    countryCode: patient?.countryCode || '',
    socialSecurityNumber: patient?.socialSecurityNumber || '',
    mobilePhone: patient?.mobilePhone || '',
    homePhone: patient?.homePhone || '',
    email: patient?.email || '',
    allergies: patient?.allergies || '',
    genderId: patient?.gender.id || '',
    ethnicityId: patient?.ethnicity.id || '',
    pathologiesIds: patient?.pathologies.map((pathology) => pathology.id) || [],
    medicalOrganizationId: patient?.medicalOrganizationId || undefined,
    medicalDoctorId: patient?.medicalDoctorId || undefined,
    mentalState: patient?.mentalState || undefined,
  };

  const form = useFormEnhanced({
    initialValues,
    normalizeFields: {
      capitalizeFields: [
        'firstName',
        'lastName',
        'locality',
        'streetAddress1',
        'streetAddress2',
      ],
      uppercaseFields: ['socialSecurityNumber'],
      lowercaseFields: ['email'],
    },
  });

  const [createPatient, { isLoading: isLoadingCreate }] =
    useCreatePatientMutation();
  const [updatePatient, { isLoading: isLoadingUpdate }] =
    useUpdatePatientMutation();

  const onCancelClick = () => {
    closeModal(UPSERT_PATIENT_FORM_MODAL_ID);
  };

  const onSubmit = async (values: typeof initialValues) => {
    try {
      let newPatient;

      const mappedValues = {
        firstName: values.firstName,
        lastName: values.lastName,
        title: values.title,
        birthDate: values.birthDate
          ? new Date(values.birthDate).toISOString()
          : undefined,
        streetAddress1: values.streetAddress1,
        streetAddress2:
          values.streetAddress2.trim() !== '' ? values.streetAddress2 : null,
        postalCode:
          values.postalCode.trim() !== ''
            ? values.countryCode === 'IT'
              ? values.postalCode
              : null
            : null,
        locality: values.locality,
        stateProvince:
          values.stateProvince.trim() !== ''
            ? values.countryCode === 'IT'
              ? values.stateProvince
              : null
            : null,
        countryCode: values.countryCode,
        socialSecurityNumber:
          values.socialSecurityNumber.trim() !== ''
            ? values.socialSecurityNumber
            : null,
        mobilePhone:
          values.mobilePhone.trim() !== '' ? values.mobilePhone : null,
        homePhone: values.homePhone.trim() !== '' ? values.homePhone : null,
        email: values.email,
        genderId: values.genderId,
        ethnicityId: values.ethnicityId,
        pathologiesIds: values.pathologiesIds,
        medicalOrganizationId: values.medicalOrganizationId,
        medicalDoctorId: values.medicalDoctorId,
        allergies: values.allergies,
        mentalState: values.mentalState,
      };

      if (patient) {
        const data: UpdatePatientRequest = {
          id: patient.id,
          body: mappedValues,
        };

        newPatient = await updatePatient(data).unwrap();

        notifications.show({
          title: 'Paziente modificato',
          message: 'Paziente modificato con successo.',
        });
      } else {
        const data: CreatePatientRequestBody = {
          ...mappedValues,
          birthDate: mappedValues.birthDate!,
        };

        newPatient = await createPatient(data).unwrap();

        notifications.show({
          title: 'Paziente creato',
          message: 'Paziente creato con successo.',
        });
      }

      closeModal(UPSERT_PATIENT_FORM_MODAL_ID);

      afterSubmit?.(newPatient);
    } catch (e) {
      handleSubmitError(e, form);
    }
  };

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Grid gutter="xl">
        <Grid.Col span={{ md: 4 }}>
          <Stack gap="sm">
            <Divider label="Dati personali" labelPosition="left" />
            <TextInput
              data-autofocus
              required
              autoCapitalize="words"
              label="Nome"
              {...form.getInputProps('firstName')}
            />
            <TextInput
              required
              label="Cognome"
              {...form.getInputProps('lastName')}
            />
            <TextInput
              required
              label="Titolo"
              {...form.getInputProps('title')}
            />
            <TextInput
              label="Codice fiscale"
              {...form.getInputProps('socialSecurityNumber')}
            />
            <DateInput
              required
              label="Data di nascita"
              valueFormat="DD/MM/YYYY"
              dateParser={(value) => parser.fromString(value, 'it')}
              {...form.getInputProps('birthDate')}
            />
            <GenderSelector
              required
              label="Genere"
              {...form.getInputProps('genderId')}
            />
            <EthnicitySelector
              required
              label="Etnia"
              {...form.getInputProps('ethnicityId')}
            />
            <PathologiesSelector
              required
              multiple
              label="Patologie"
              {...form.getInputProps('pathologiesIds')}
            />
            <TextInput label="Allergie" {...form.getInputProps('allergies')} />
            <Text size="sm" fw={500}>
              Mental state
            </Text>
            <Slider
              label={form.values.mentalState}
              min={0}
              max={30}
              marks={[
                { value: 0, label: 'Grave' },
                { value: 10, label: 'Moderato' },
                { value: 19, label: 'Lieve' },
                { value: 24, label: 'Normale' },
              ]}
              {...form.getInputProps('mentalState')}
            />
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ md: 4 }}>
          <Stack gap="sm">
            <Divider label="Indirizzo" labelPosition="left" />
            <NationSelector required {...form.getInputProps('countryCode')} />
            {form.values.countryCode === 'IT' ? (
              <>
                <ProvinceSelector
                  required
                  {...form.getInputProps('stateProvince')}
                />
                <CitySelector
                  required
                  province={form.values.stateProvince}
                  {...form.getInputProps('locality')}
                  disabled={form.values.stateProvince === ''}
                />
                <ZipSelector
                  required
                  city={form.values.locality}
                  {...form.getInputProps('postalCode')}
                  disabled={form.values.locality === ''}
                />
              </>
            ) : (
              <>
                <TextInput
                  required
                  label="Città"
                  {...form.getInputProps('locality')}
                />
              </>
            )}
            <TextInput
              required
              label="Indirizzo"
              {...form.getInputProps('streetAddress1')}
            />
            <TextInput
              label="Indirizzo (2)"
              {...form.getInputProps('streetAddress2')}
            />
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ md: 4 }}>
          <Stack gap="sm">
            <Divider label="Contatti" labelPosition="left" />
            {canReadAllMedicalOrganizations && (
              <MedicalOrganizationsSelector
                label="Organizzazione medica"
                {...form.getInputProps('medicalOrganizationId')}
              />
            )}
            {canReadAllDoctors && (
              <MedicalDoctorsSelector
                label="Medico"
                {...form.getInputProps('medicalDoctorId')}
              />
            )}
            <TextInput
              type="email"
              label="Email"
              description={
                patient
                  ? null
                  : "Verrà inviata un'email con le istruzioni per accedere"
              }
              required
              {...form.getInputProps('email')}
            />
            <TextInput
              label="Telefono cellulare"
              {...form.getInputProps('mobilePhone')}
            />
            <TextInput
              label="Telefono fisso"
              {...form.getInputProps('homePhone')}
            />
          </Stack>
        </Grid.Col>
      </Grid>

      {form.errors.general && (
        <Text c="red" size="xs" ta="right">
          {form.errors.general}
        </Text>
      )}

      <Group justify="right" mt="xl">
        <Button
          onClick={onCancelClick}
          variant="default"
          loading={isLoadingCreate || isLoadingUpdate}
        >
          Annulla
        </Button>
        <Button type="submit" loading={isLoadingCreate || isLoadingUpdate}>
          Conferma
        </Button>
      </Group>
    </form>
  );
}
